import { CallApiOptions } from './api';
import { AppState } from '../store/reducers';
import {
  ApiAppConfig,
  ApiAttributeFilter,
  ApiCluster,
  ApiElement,
  ApiRow,
  ApiSourceCategory,
  ApiStory,
  ApiTargetCategory,
  mapAppConfig,
} from './story.services';
import {
  AppConfig,
  AttributeFilter,
  Row,
  SourceCategory,
  TargetCategory,
  Element,
} from '../store/Entities/Stories/stories.reducer';
import { Cluster } from '../store/Entities/Clusters/clusters.reducer';
import { getActiveStoryClusters } from '../store/StoryEdit/story-edit.reducer';
import { TYPE_CATBRAND } from '../modules/StoryEdit/components/Stream/TileTargetMenu/index';
import { isEmpty, map, forEachObjIndexed, contains, values } from 'ramda';

export const storySaveCallOptions = (state: AppState): CallApiOptions => {
  const {
    storyEdit: { story },
  } = state;

  const saveData = mapToApi(state);

  return {
    method: 'PUT',
    endpoint: `stories/${story.id}`,
    params: {
      include:
        'clusters,appConfigs.sourceCategories,targetCategories,rows,appConfigs.errors',
    },
    body: JSON.stringify({
      ...saveData,
    }),
  };
};

function mapToApi(state: AppState): ApiStory {
  const {
    storyEdit: { story },
  } = state;
  const clusters = getActiveStoryClusters(state);

  return {
    id: story.id,
    name: story.name,
    color: story.color,
    type: story.type,
    initial_app_id: story.initialAppId,
    resource_id: story.resourceId || null,
    resource_type: story.resourceType || null,
    content_type: story.contentType || null,
    headline: story.headline || null,
    subline: story.subline || null,
    headline_long: story.headlineLong || null,
    subline_long: story.sublineLong || null,
    gender: story.gender || null,
    age_from: story.age_from || null,
    age_to: story.age_to || null,
    appConfigs: { data: mapAppConfigs(state) },
    rows: { data: mapRows(story.rows) },
    clusters: { data: mapClusters(clusters) },
    header_image_brand_id: story.headerImageBrandId,
    larger_desktop_teaser: story.largerDesktopTeaser,
    appImage: story.appImage,
    mobileImage: story.mobileImage,
    desktopImage: story.desktopImage,
    ownerImage: story.ownerImage,
    is_primary: story.isPrimary,
    is_sponsored: story.isSponsored,
    is_bas_sponsored: story.isBasSponsored,
    is_sale: story.isSale,
    is_premium: story.isPremium,
    is_hidden: story.isHidden,
    is_product_row_disabled: story.isProductRowDisabled,
    is_secondhand: story.isSecondhand,
  };
}

function mapClusters(clusters: Cluster[]): ApiCluster[] {
  if (isEmpty(clusters)) {
    return [];
  }

  return map(
    (cluster: Cluster): ApiCluster => {
      return {
        id: cluster.id,
        name: cluster.name,
      };
    },
    clusters as Cluster[]
  );
}

function mapRows(rows: Row[]): ApiRow[] {
  const data: ApiRow[] = [];
  forEachObjIndexed(
    (row: Row, key) => {
      const isImage = contains(row.type, ['tile', 'double tile', 'layout']);

      data.push({
        id: row.id,
        position: row.position,
        type: row.type,
        elements: { data: mapElements(row.elements) },
        ...(isImage ? { mobile_tile_ratio: row.mobileRatio || 1 } : {}),
        ...(isImage ? { desktop_tile_ratio: row.desktopRatio || 1 } : {}),
        row_count: row.rowsCount,
        double_tile_shifted: row.doubleTileShifted ? 1 : 0,
        layout_order: row.layoutOrder,
      });
    },
    rows as any
  );

  return data;
}

function mapElements(elements: Element[]): ApiElement[] {
  return map((element: Element) => {
    return {
      id: element.id,
      type: element.type === 'product row' ? 'product' : element.type,
      position: element.position,
      header_image_brand_id: element.headerImageBrandId,
      larger_desktop_teaser: element.largerDesktopTeaser,
      appImage: element.appImage,
      mobileImage: element.mobileImage,
      mobileVideo: element.mobileVideo,
      desktopImage: element.desktopImage,
      desktopVideo: element.desktopVideo,
      text: element.text,
      resource_type: element.resourceType,
      resource_id: element.resourceId,
      url: element.url,
      additional_resource_data:
        element.additionalResourceData &&
        element.resourceType &&
        element.resourceType === TYPE_CATBRAND
          ? { brand_id: element.additionalResourceData.brandId }
          : void 0,
      tracking_id: element.trackingId,
    };
  }, elements);
}

function mapAppConfigs(state: AppState): ApiAppConfig[] {
  const {
    storyEdit: { appConfigs },
  } = state;
  const data: ApiAppConfig[] = [];

  // outfitIds need to be saved for all locales
  const appConfigDE = values(appConfigs).find(appConfig => appConfig.app_id === 139);
  const outfitIds = appConfigDE && appConfigDE.outfit_ids;

  forEachObjIndexed(
    (item: AppConfig, key) =>
      data.push({
        ...mapAppConfig(item, outfitIds),
        sourceCategories: {
          data: mapSourceCategories(item),
        },
        targetCategories: {
          data: mapTargetCategories(item),
        },
      }),
    appConfigs
  );

  return data;
}

function mapTargetCategories(appConfig: AppConfig): ApiTargetCategory[] {
  if (!appConfig.targetCategories || appConfig.targetCategories.length === 0) {
    return [];
  }

  return map((targetCategory: TargetCategory): ApiTargetCategory => {
    return {
      id: targetCategory.id as number,
      category_id: targetCategory.category_id,
      is_pinned: targetCategory.isPinned,
    };
  }, appConfig.targetCategories);
}

function mapSourceCategories(appConfig: AppConfig): ApiSourceCategory[] {
  if (!appConfig.sourceCategories || appConfig.sourceCategories.length === 0) {
    return [];
  }

  return map((sourceCategory: SourceCategory): ApiSourceCategory => {
    return {
      id: sourceCategory.id,
      category_id: sourceCategory.categoryId,
      attributeFilters: { data: mapAttributeFilters(sourceCategory) },
    };
  }, appConfig.sourceCategories);
}

function mapAttributeFilters(
  sourceCategory: SourceCategory
): ApiAttributeFilter[] {
  if (
    !sourceCategory.attributeFilters ||
    sourceCategory.attributeFilters.length === 0
  ) {
    return [];
  }

  return map((attrFilter: AttributeFilter): ApiAttributeFilter => {
    return {
      attribute_group_id: attrFilter.attributeGroupId,
      id: attrFilter.id,
      attribute_ids: attrFilter.attributeIds,
    };
  }, sourceCategory.attributeFilters);
}



// WEBPACK FOOTER //
// ./src/services/storyedit.services.ts